import {FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useLocation, useMatch, useNavigate, useParams} from "react-router-dom";
import {Alert, Box, Button, CircularProgress, Tab, Tabs, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {ModuleTab} from "@buildwitt/shapeshift-common/v1/module";
import {NotesSection} from "./NotesSection";
import {PageTitle} from "@buildwitt/shapeshift-common/v1/pageBase";
import {Files} from "@buildwitt/shapeshift-common/v1/files";
import {CommentsSection} from "./CommentsSection";
import {App2NotificationContext, App2NotificationSeverity} from "@/app2/shared/contexts/App2NotificationContext";
import {CenteredContainer} from "@/components/pageBase/centeredContainer";
import {
	App2LessonVideoPlayer,
	LESSON_VIDEO_NEAR_END_TOLERANCE_SEC,
	LESSON_VIDEO_SEEK_AT_END_OFFSET_SEC
} from "./LessonVideoPlayer";
import type {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v1/wysiwyg";
import {IconDocSearch} from "@/icons/IconDocSearch";
import {IconComments} from "@/icons/IconComments";
import {IconNotes} from "@/icons/IconNotes";
import {palette} from "@/constants";
import {Player, PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import {MiniQuizzer} from "./MiniQuizzer";
import StreakDrawer from "./StreakDrawer/StreakDrawer";
import {CompletionCelebrationWrap} from "./CompletionCelebration/CompletionCelebrationWrap";
import {useGetCourseDetails} from "@/app2/shared/data";
import ResumeStreakDrawer from "./ResumeStreakDrawer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import {getAllFontStyles} from "@/templates/defaultTheme";
import {
	useCompleteLesson,
	useFlushLessonVideoProgress,
	useGetCourseCurriculum,
	getLessonForCourseQueryKey,
	LESSON_VIDEO_PROGRESS_SOCKET_EVENT,
	type LessonVideoProgressMessage,
	type CompleteLessonResponse,
	type GetLessonForCourseResponse
} from "@/app2/shared/data";
import {useWebSocket} from "@/app2/shared/contexts/WebSocketContext";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";
import {useContentContainerRef} from "@/app2/shared/contexts/ContentContainerContext";
import {useMemberedCohort} from "@/app2/shared/hooks";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {bitmovinPlayerConfig} from "@/constants";
import EngineBitmovinModule from "bitmovin-player/modules/bitmovinplayer-engine-bitmovin";
import MseRendererModule from "bitmovin-player/modules/bitmovinplayer-mserenderer";
import HlsModule from "bitmovin-player/modules/bitmovinplayer-hls";
import DashModule from "bitmovin-player/modules/bitmovinplayer-dash";
import AbrModule from "bitmovin-player/modules/bitmovinplayer-abr";
import XmlModule from "bitmovin-player/modules/bitmovinplayer-xml";
import ContainerTSModule from "bitmovin-player/modules/bitmovinplayer-container-ts";
import ContainerMp4Module from "bitmovin-player/modules/bitmovinplayer-container-mp4";
import SubtitlesModule from "bitmovin-player/modules/bitmovinplayer-subtitles";
import SubtitlesCEA608Module from "bitmovin-player/modules/bitmovinplayer-subtitles-cea608";
import PolyfillModule from "bitmovin-player/modules/bitmovinplayer-polyfill";
import StyleModule from "bitmovin-player/modules/bitmovinplayer-style";
import "bitmovin-player/bitmovinplayer-ui.css";
import {UIFactory} from "bitmovin-player-ui";

const useStyles = makeStyles<{ scrollToContinueBannerEnabled: boolean }>()((theme, {scrollToContinueBannerEnabled}) => ({
	miniQuizzer: {
		"[class*='submitBtn']": {
			[theme.breakpoints.down("sm")]: {
				width: "100%"
			}
		}
	},
	title: {
		color: theme.palette.common.white,
		"h2": {
			fontSize: "42px"
		},
		"h5": {
			fontSize: "20px",
			lineHeight: "1.5rem",
			marginBottom: "20px"
		},
		[theme.breakpoints.down("sm")]: {
			marginTop: "0px",
			marginBottom: "0px",
			paddingTop: "0px",
			"h2": {
				fontSize: "32px"
			},
			"h5": {
				fontSize: "16px",
				lineHeight: "1.5rem",
				marginBottom: "0px"
			},
			"&:not(.hasButton) h5": {
				marginBottom: "20px"
			},
			"&.hasButton h5": {
				marginBottom: "0px"
			},
			"&.collapsed h5": {
				display: "-webkit-box",
				WebkitLineClamp: 2,
				WebkitBoxOrient: "vertical",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "normal"
			}
		}
	},
	centeredContainer: {
		maxWidth: "900px",
		padding: "0 20px",
		paddingBottom: "100px"
	},
	lessonCompleteBtn: {
		width: "200px",
		textTransform: "uppercase",
		[theme.breakpoints.down("sm")]: {
			width: "100% !important",
			float: "none",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			textAlign: "center",
			borderRadius: "999px",
			"&.MuiButton-contained": {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.common.black,
				boxShadow: `0 0 20px ${theme.palette.primary.main}40, 0 2px 4px rgba(0, 0, 0, 0.2)`
			},
			"&.MuiButton-outlined": {
				backgroundColor: palette.greySolid,
				borderColor: palette.greySolid,
				color: palette.grey60,
				"&:hover": {
					backgroundColor: palette.greySolid,
					borderColor: palette.greySolid
				}
			}
		},
		float: "right"
	},
	content: {
		...getAllFontStyles(theme),
		color: theme.palette.text.primary,
		marginBottom: "40px",
		"& p": {
			lineHeight: 1.5,
			fontSize: "17px"
		},
		"& a": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	viewMoreButton: {
		display: "none",
		[theme.breakpoints.down("sm")]: {
			display: "flex",
			alignItems: "center",
			gap: "4px",
			marginTop: "0px",
			marginBottom: "20px",
			color: theme.palette.common.white,
			opacity: 0.5,
			textTransform: "none",
			padding: 0,
			minWidth: "auto",
			fontSize: "14px",
			background: "transparent",
			boxShadow: "none",
			border: "none",
			transition: "none",
			position: "relative",
			top: "0px",
			"&:hover": {
				background: "transparent",
				boxShadow: "none",
				border: "none"
			},
			"&:focus": {
				outline: "none",
				border: "none"
			},
			"&:active": {
				background: "transparent",
				boxShadow: "none"
			},
			"& .MuiTouchRipple-root": {
				display: "none"
			},
			"& .chevron": {
				transition: "transform 0.2s ease-in-out",
				fontSize: "20px"
			},
			"& .chevron.expanded": {
				transform: "rotate(180deg)"
			}
		}
	},
	attachmentsSectionTitle: {
		color: "#FFFFFF",
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 600,
		fontSize: "20px",
		lineHeight: "150%",
		letterSpacing: "0.15px",
		margin: "20px 0"
	},
	files: {
		marginBottom: "40px",
		"& > div": {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			background: "transparent",
			padding: "12px 0",
			marginBottom: "8px",
			gap: "8px",
			position: "relative",
			paddingLeft: "22px"
		},
		"& > div::before": {
			content: "\"\"",
			position: "absolute",
			left: 0,
			top: "50%",
			transform: "translateY(-50%)",
			width: "18px",
			height: "18px",
			flexShrink: 0,
			backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${theme.palette.primary.main}" d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5C7 20.54 9.46 23 12.5 23s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>`
			)}")`,
			backgroundRepeat: "no-repeat",
			backgroundPosition: "center",
			backgroundSize: "contain"
		},
		"& > div > div:first-of-type": {
			display: "none"
		},
		"& > div > div:nth-of-type(2)": {
			margin: "0 !important",
			flexGrow: 1,
			minWidth: 0
		},
		"& > div > div:nth-of-type(2) h6": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			color: theme.palette.primary.main,
			margin: 0
		},
		"& > div > a": {
			position: "absolute",
			left: 0,
			top: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
			border: "none",
			padding: 0,
			background: "transparent"
		},
		"& > div > a > div": {
			visibility: "hidden"
		}
	},
	tabs: {
		width: "100%",
		margin: 0,
		marginBottom: "50px",
		"[class*='indicator']": {
			display: "none"
		},
		[theme.breakpoints.up("sm")]: {
			"[class*='MuiTabs-scroller']": {
				marginLeft: "25px"
			}
		},
		[theme.breakpoints.down("sm")]: {
			"[class*='MuiTabs-scroller']": {
				marginLeft: "15px"
			},
			gap: 0,
			marginBottom: "10px"
		}
	},
	activeTab: {
		color: theme.palette.common.white + "!important",
		background: "transparent"
	},
	tab: {
		display: "flex",
		fontSize: "14px",
		gap: "5px",
		justifyContent: "space-around",
		color: "#A8A8A8",
		padding: "20px 12px",
		letterSpacing: "0.5px",
		"& svg": {
			marginTop: "4px"
		},
		"& svg path": {
			fill: palette.midGrey
		},
		[theme.breakpoints.down("sm")]: {
			minHeight: "44px",
			fontSize: "13px",
			padding: "0 5px"
		}
	},
	comments: {
		"& > div": {
			background: palette.drawer
		},
		"[class*='MuiAvatar']": {
			background: palette.grey10
		},
		"& *": {
			color: theme.palette.common.white + "!important"
		}
	},
	notes: {
		"& *": {
			color: theme.palette.common.white + "!important"
		},
		"& [class*='inputContainer'] > [class*='MuiTextField']": {
			background: palette.drawer
		},
		"[class*='filtersContainer'] > div": {
			background: palette.grey10
		}
	},
	notesSection: {
		"& [class*='timeStampInputAdornment']": {
			alignSelf: "start",
			marginTop: "5px"
		}
	},
	scrollToContinueBanner: {
		opacity: scrollToContinueBannerEnabled ? 1 : 0,
		transition: "opacity 0.2s ease-in-out",
		position: "absolute",
		color: theme.palette.primary.main,
		width: "100%",
		textAlign: "center",
		paddingTop: "35%",
		height: "100%",
		pointerEvents: "none",
		justifyContent: "center",
		margin: "auto",
		zIndex: scrollToContinueBannerEnabled ? 10 : -1,
		display: scrollToContinueBannerEnabled ? "flex" : "none",
		"& svg": {
			fontSize: 50,
			padding: 0,
			margin: 0,
			[theme.breakpoints.down("sm")]: {
				fontSize: 30
			}
		},
		"& > *": {
			pointerEvents: scrollToContinueBannerEnabled ? "auto" : "none",
			cursor: scrollToContinueBannerEnabled ? "pointer" : "default"
		},
		background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2))"
	}
}));

interface IProps {
	lesson: GetLessonForCourseResponse;
}

/**
 * Lesson Page Content
 *
 * Renders the full lesson experience:
 * - Video player (Bitmovin)
 * - Tabs (Overview, Comments, Notes)
 * - Content, files
 * - Mini quiz (if attached)
 * - Complete lesson button
 *
 * Uses React Query for mutations instead of context providers.
 */
export const LessonPageContent: FC<IProps> = ({lesson}) => {
	const {t} = useSSTranslation();
	const MODULE_TABS: { label: JSX.Element, value: ModuleTab }[] = [
		{
			label: <><IconDocSearch /> {t("module_tab_overview", "OVERVIEW")}</>,
			value: "overview"
		},
		{
			label: <><IconComments /> {t("module_tab_comments", "COMMENTS")}</>,
			value: "comments"
		},
		{
			label: <><IconNotes /> {t("module_tab_notes", "NOTES")}</>,
			value: "notes"
		}
	];

	const notesTranslations = {
		mostRecent: t("notes.most_recent", "Most recent"),
		oldestFirst: t("notes.oldest_first", "Oldest first"),
		noteInputPlaceholder: t("notes.input_placeholder", "Write your private note")
	};

	const commentsTranslations = {
		loadMore: t("comments.load_more", "Load more"),
		commentInputPlaceholder: t("comments.input_placeholder", "Leave a comment"),
		replyToUser: t("comments.reply_to_user", "Reply to {userName}"),
		replies: t("comments.replies", "replies"),
		today: t("comments.today", "Today"),
		yesterday: t("comments.yesterday", "Yesterday"),
		daysAgo: t("comments.days_ago", "{count} days ago")
	};

	const [videoPlaying, setVideoPlaying] = useState(false);
	const [miniQuizzerDisabled, setMiniQuizzerDisabled] = useState(true);
	const [scrollToContinueBannerEnabled, setScrollToContinueBannerEnabled] = useState(false);
	const {cx, classes} = useStyles({scrollToContinueBannerEnabled});
	const pageIsFeed = useMatch("/feed/:courseSlug/*");
	const {courseSlug, moduleSlug: lessonSlug} = useParams<{ courseSlug: string; moduleSlug: string }>();

	// Get membered cohort from React Query hook instead of context
	const memberedCohort = useMemberedCohort();

	// Use scrollable container from PageBase when available (via context), else fallback to DOM search
	const contextContainerRef = useContentContainerRef();
	const localContainerRef = useRef<HTMLElement | null>(null);
	const contentContainerRef = contextContainerRef ?? localContainerRef;

	const {notify} = useContext(App2NotificationContext);
	const navigate = useNavigate();
	const [tabSelected, setTab] = useState<ModuleTab>("overview");
	const videoRef = useRef<VideoPlayerRef | null>(null);
	const [player, setPlayer] = useState<PlayerAPI | null>(null);
	const [videoFinished, setVideoFinished] = useState(false);
	const location = useLocation();
	const [quizComplete, setQuizComplete] = useState(false);
	const videoContainerRef = useRef<HTMLDivElement | null>(null);
	const [isSubtitleExpanded, setIsSubtitleExpanded] = useState(false);
	const [needsTruncation, setNeedsTruncation] = useState(false);
	const [openStreakDrawer, setOpenStreakDrawer] = useState(false);
	const [streakDays, setStreakDays] = useState<number>(0);
	const [openCelebration, setOpenCelebration] = useState(false);
	const [pendingStreak, setPendingStreak] = useState<{ days: number } | null>(null);
	const [pendingResumeStreak, setPendingResumeStreak] = useState(false);
	const [isStreakPaused, setIsStreakPaused] = useState(false);
	const queryClient = useQueryClient();

	// React Query mutation for completing lesson
	const {mutate: completeLesson, isPending: isCompletingLesson} = useCompleteLesson();

	const [{locale}] = useContext(LocaleContext);
	const curriculumParams = {locale: resolveLocale(locale)};

	// Fetch curriculum to find next lesson
	const {data: curriculumData} = useGetCourseCurriculum(courseSlug!, curriculumParams);
	const {data: courseDetails} = useGetCourseDetails(courseSlug!, curriculumParams);
	const firstAvailableModule = curriculumData?.items.find(
		item => !item.complete && !item.isLocked
	);

	// When not inside PageBase (no context), find scrollable container among body's descendants
	useEffect(() => {
		if (contextContainerRef) return;

		const findScrollableDescendant = (element: HTMLElement): HTMLElement | null => {
			const style = window.getComputedStyle(element);
			if (style.overflowY === "auto" || style.overflowY === "scroll") return element;
			for (let i = 0; i < element.children.length; i++) {
				const found = findScrollableDescendant(element.children[i] as HTMLElement);
				if (found) return found;
			}
			return null;
		};

		localContainerRef.current = findScrollableDescendant(document.body);
	}, [contextContainerRef]);

	/** When saved progress is in the "finished" window but the lesson is not yet marked complete, enable quiz/continue UI. */
	const resumeTimeSecondsForPlayer = useMemo((): number | undefined => {
		const fromNav = (location.state as { timestamp?: number } | null | undefined)?.timestamp;
		if (fromNav != null) {
			return fromNav;
		}
		const {complete, videoTime, videoDuration, videoUrl} = lesson;
		if (complete && videoTime != null && videoDuration != null && videoTime >= videoDuration) {
			return videoDuration;
		}
		if (videoUrl && videoTime != null && videoDuration != null) {
			if (videoTime >= videoDuration - LESSON_VIDEO_NEAR_END_TOLERANCE_SEC) {
				return Math.max(0, videoDuration - LESSON_VIDEO_SEEK_AT_END_OFFSET_SEC);
			}
		}
		return videoTime ?? undefined;
	}, [location.state, lesson]);

	/** Only when the route lesson changes. Reset UI state so a prior lesson cannot leak completion/quiz state. */
	useEffect(() => {
		setScrollToContinueBannerEnabled(false);
		setIsSubtitleExpanded(false);
		setVideoFinished(false);
		setQuizComplete(false);
		setMiniQuizzerDisabled(true);
	}, [lessonSlug]);

	useEffect(() => {
		if (lesson.complete) {
			setVideoFinished(true);
			setQuizComplete(true);
			return;
		}
		const vdur = lesson.videoDuration;
		const vtime = lesson.videoTime;
		const atEndWatchedNoComplete =
			!!lesson.videoUrl && vdur != null && vtime != null
			&& vtime >= vdur - LESSON_VIDEO_NEAR_END_TOLERANCE_SEC;
		if (atEndWatchedNoComplete) {
			setVideoFinished(true);
		} else {
			setVideoFinished(!lesson.videoUrl);
		}
		setQuizComplete(!lesson.quiz);
	}, [lessonSlug, lesson.complete, lesson.videoUrl, lesson.quiz, lesson.videoTime, lesson.videoDuration]);

	// Check if the subtitle is truncated. Rerun on resize.
	useEffect(() => {
		if (!lesson?.subtitle || !contentContainerRef?.current || isSubtitleExpanded) {
			if (!lesson?.subtitle) {
				setNeedsTruncation(false);
			}
			return;
		}

		const MOBILE_BREAKPOINT = 700; // Matches theme.breakpoints.down("sm")

		const checkTruncation = (): void => {
			// Only check on mobile viewport
			if (window.innerWidth >= MOBILE_BREAKPOINT) {
				setNeedsTruncation(false);
				return;
			}

			const container = contentContainerRef?.current;
			if (!container) return;

			const subtitleElement = container.querySelector("[class*=\"title\"] h5") as HTMLElement | null;
			if (!subtitleElement) {
				setNeedsTruncation(false);
				return;
			}

			// Check if content is truncated by comparing scrollHeight to clientHeight
			const isTruncated = subtitleElement.scrollHeight > subtitleElement.clientHeight;
			setNeedsTruncation(isTruncated);
		};

		checkTruncation();
		window.addEventListener("resize", checkTruncation);

		return () => {
			window.removeEventListener("resize", checkTruncation);
		};
	}, [lesson?.subtitle, isSubtitleExpanded]);

	useEffect(() => {
		const updateScrollBanner = (): void => {
			const container = contentContainerRef?.current;
			if (!container) return;

			const scrollableDistance = container.scrollHeight - container.clientHeight;
			const hasScrollDistance = scrollableDistance > 50;

			setScrollToContinueBannerEnabled(!videoPlaying && !miniQuizzerDisabled && hasScrollDistance && !lesson.complete);
		};

		updateScrollBanner();
		window.addEventListener("resize", updateScrollBanner);

		return () => {
			window.removeEventListener("resize", updateScrollBanner);
		};
	}, [videoPlaying, miniQuizzerDisabled, lesson.complete]);

	// When "scroll to continue" banner is shown, exit fullscreen so the user can see it and scroll
	useEffect(() => {
		if (!scrollToContinueBannerEnabled) return;
		const doc = document as Document & {
			webkitFullscreenElement?: Element;
			webkitExitFullscreen?: () => Promise<void>;
			webkitCancelFullScreen?: () => void;
			mozCancelFullScreen?: () => void;
			msExitFullscreen?: () => void;
		};
		const isFullscreen = Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement);
		if (isFullscreen) {
			const exit =
				doc.exitFullscreen ??
				doc.webkitExitFullscreen ??
				doc.webkitCancelFullScreen ??
				doc.mozCancelFullScreen ??
				doc.msExitFullscreen;
			if (exit) {
				const result = exit.call(doc);
				if (result?.catch) result.catch(() => {});
			}
		}
	}, [scrollToContinueBannerEnabled]);

	// Keep ref in sync for cleanup (avoids effect running when player changes, which can interrupt play())
	const playerRef = useRef<PlayerAPI | null>(null);
	playerRef.current = player;

	// Destroy Bitmovin player when lesson changes (fixes black video when navigating back)
	// Pause before destroy + defer to next tick to avoid "media was removed from the document" error
	useEffect(() => {
		return () => {
			const p = playerRef.current;
			if (p) {
				playerRef.current = null;
				setTimeout(() => {
					try {
						p.pause?.();
						p.destroy();
					} catch {
						// ignore cleanup errors
					}
				}, 0);
			}
		};
	}, [lessonSlug]);

	useEffect(() => {
		videoRef.current = null;
		setPlayer(null);
		if (contentContainerRef?.current) {
			contentContainerRef.current.scrollTo({top: 0, behavior: "smooth"});
		}
	}, [lessonSlug]);

	const handleVideoReady = useCallback(() => {
		if (!process.env.REACT_APP_BITMOVIN_PLAYER_KEY)
			throw new Error("REACT_APP_BITMOVIN_PLAYER_KEY required");

		if (!videoRef.current?.bitmovin?.container)
			return;

		// Destroy existing player before creating new one (fixes black video when navigating back)
		if (playerRef.current) {
			try {
				playerRef.current.pause?.();
				playerRef.current.destroy();
			} catch {
				// ignore
			}
		}

		const playerSource = {
			hls: lesson.videoUrl || "",
			poster: lesson.videoThumbnailUrl || undefined
		};

		Player.addModule(EngineBitmovinModule);
		Player.addModule(MseRendererModule);
		Player.addModule(HlsModule);
		Player.addModule(XmlModule);
		Player.addModule(DashModule);
		Player.addModule(AbrModule);
		Player.addModule(ContainerTSModule);
		Player.addModule(ContainerMp4Module);
		Player.addModule(SubtitlesModule);
		Player.addModule(SubtitlesCEA608Module);
		Player.addModule(PolyfillModule);
		Player.addModule(StyleModule);

		// Delay player setup for Safari
		setTimeout(() => {
			if (!videoRef.current?.bitmovin?.container)
				return;

			const playerInstance = new Player(videoRef.current.bitmovin?.container, bitmovinPlayerConfig);
			UIFactory.buildDefaultUI(playerInstance);
			playerInstance.load(playerSource).then(() => {
				setPlayer(playerInstance);
			}, (e) => {
				console.error("Error while loading source", e);
			});
		}, 100); // Delay added
	}, [lesson.videoUrl, lesson.videoThumbnailUrl, lessonSlug]);

	const navigateToNext = () => {
		if (pageIsFeed) {
			navigate("/feed/");
		} else {
			navigate("/course/" + courseSlug);
		}
	};

	const handleCompletion = useCallback(() => {
		if (!lesson.enrollmentId) {
			notify("Please try again", "Something went wrong", App2NotificationSeverity.error, 5000);
			return;
		}

		completeLesson(
			{enrollmentId: lesson.enrollmentId},
			{
				onSuccess: (response: CompleteLessonResponse) => {
					notify(null, "Lesson completed", App2NotificationSeverity.success, 5000);

					if (courseSlug && lessonSlug) {
						queryClient.invalidateQueries({queryKey: getLessonForCourseQueryKey(courseSlug, lessonSlug)});
					}

					// Course just completed: show celebration first, defer streak/resume drawers
					if (response?.courseCompleted) {
						setOpenCelebration(true);
						if (response?.isStreakPaused) {
							setPendingResumeStreak(true);
							if (typeof response.streakDays === "number") {
								setPendingStreak({days: response.streakDays});
							}
						} else if (response?.isStreakChanged && typeof response.streakDays === "number") {
							setPendingStreak({days: response.streakDays});
						}
						return;
					}

					// Streak is paused (course not completed): show resume prompt
					if (response?.isStreakPaused) {
						if (typeof response.streakDays === "number") {
							setPendingStreak({days: response.streakDays});
						}
						setIsStreakPaused(true);
						return;
					}

					// Regular lesson complete with streak increment: show streak drawer
					if (response?.isStreakChanged && typeof response.streakDays === "number") {
						setOpenStreakDrawer(true);
						setStreakDays(response.streakDays);
						return;
					}

					navigateToNext();
				},
				onError: () => {
					notify("Please try again", "Something went wrong", App2NotificationSeverity.error, 5000);
				}
			}
		);
	}, [lesson.enrollmentId, completeLesson, notify, navigateToNext, courseSlug, lessonSlug, queryClient]);

	const handleCompleteCheck = (): boolean => {
		if (lesson.videoUrl && !videoFinished) {
			notify("Please finish the video before progressing", "Hold up!", App2NotificationSeverity.warning, 5000);
			return false;
		}
		if (lesson.quiz && !quizComplete) {
			notify("Please attempt the quiz before progressing", "Hold up!", App2NotificationSeverity.warning, 5000);
			return false;
		}

		return true;
	};

	// Check if lesson can be completed (without side effects like notify)
	const canComplete = useMemo(() => {
		if (lesson.videoUrl && !videoFinished) return false;
		if (lesson.quiz && !quizComplete) return false;
		return true;
	}, [lesson.videoUrl, lesson.quiz, videoFinished, quizComplete]);

	const prevProgress = useRef<number>();
	/** Latest progress sent or to send; used to flush on unmount (navigate away). */
	const lastProgressRef = useRef<number | null>(null);
	/** Brightcove throttles onProgress to 5s; keep ref current every timeupdate so unmount flush has the real time. */
	const trackPlaybackTimeForFlush = useCallback(
		(t: number) => {
			if (lesson.enrollmentId == null) return;
			lastProgressRef.current = t;
		},
		[lesson.enrollmentId]
	);
	const {socket} = useWebSocket();
	const flushLessonVideoProgress = useFlushLessonVideoProgress();

	/** Keep last position for unmount flush even if WebSocket is not ready (matches handleReactVideoProgress for URL player). */
	const emitProgress = useCallback(
		(progress: number) => {
			if (!lesson.enrollmentId) return;
			lastProgressRef.current = progress;
			if (!socket?.emit) return;
			const message: LessonVideoProgressMessage = {
				enrollmentId: lesson.enrollmentId,
				progress
			};
			socket.emit(LESSON_VIDEO_PROGRESS_SOCKET_EVENT, message);
		},
		[lesson.enrollmentId, socket]
	);

	// Flush last known progress when leaving the page (e.g. navigate away) so progress persists.
	useEffect(() => {
		return () => {
			if (
				lastProgressRef.current != null &&
				lesson.enrollmentId != null &&
				courseSlug != null &&
				lessonSlug != null
			) {
				flushLessonVideoProgress({
					enrollmentId: lesson.enrollmentId,
					progress: lastProgressRef.current,
					courseSlug,
					lessonSlug
				});
			}
		};
	}, [lesson.enrollmentId, courseSlug, lessonSlug, flushLessonVideoProgress]);

	// BITMOVIN PLAYER PROGRESS + WebSocket video progress tracking
	// Do not gate on socket/isConnected: emitProgress still records lastProgressRef; flush or emit sends when available.
	useEffect(() => {
		if (!player) return;

		const interval = setInterval(() => {
			const started = (player?.getCurrentTime() ?? 0) > 0;
			const finished = (player.getDuration() - player.getCurrentTime()) < 5;
			if (finished) setVideoFinished(true);

			const currentTime = player.getCurrentTime();
			if (started && prevProgress.current !== currentTime) {
				prevProgress.current = currentTime;
				if (lesson.enrollmentId) {
					emitProgress(currentTime);
				}
			}

			setVideoPlaying(player.isPlaying());
		}, 500);

		return () => clearInterval(interval);
	}, [player, lesson.enrollmentId, emitProgress]);

	const handleReactVideoProgress = useCallback(
		(e: { playedSeconds: number }) => {
			lastProgressRef.current = e.playedSeconds;
			if (videoPlaying && lesson.enrollmentId && socket?.emit) {
				const message: LessonVideoProgressMessage = {
					enrollmentId: lesson.enrollmentId,
					progress: e.playedSeconds
				};
				socket.emit(LESSON_VIDEO_PROGRESS_SOCKET_EVENT, message);
			}
		},
		[videoPlaying, lesson.enrollmentId, socket]
	);

	useEffect(() => setMiniQuizzerDisabled(!videoFinished), [videoFinished]);
	useEffect(() => (lesson.complete) ? setMiniQuizzerDisabled(false) : undefined, [lesson.complete]);

	const handleClickContinue = () => {
		setOpenStreakDrawer(false);
		setStreakDays(0);
		navigateToNext();
	};

	const handleCelebrationClose = (needsToContinue: boolean) => {
		setOpenCelebration(false);
		if (pendingResumeStreak) {
			setPendingResumeStreak(false);
			setIsStreakPaused(true);
			return;
		}
		if (pendingStreak) {
			setOpenStreakDrawer(true);
			setStreakDays(pendingStreak.days);
			setPendingStreak(null);
		} else if (needsToContinue) {
			navigate("/home");
		}
	};

	const handleResumeStreakClose = (isStreakResumed: boolean, streak: number) => {
		setIsStreakPaused(false);
		if (pendingStreak && isStreakResumed) {
			setOpenStreakDrawer(true);
			setStreakDays(streak);
			setPendingStreak(null);
		} else {
			if (pageIsFeed) {
				if (curriculumData && curriculumData.progressPercent === 100) {
					navigate("/home");
				} else {
					navigateToNext();
				}
			} else {
				navigateToNext();
			}
		}
	};

	return (
		<>
			<Box ref={videoContainerRef}>
				<App2LessonVideoPlayer
					key={lessonSlug ?? lesson?.slug ?? ""}
					componentOverrides={{
						header: scrollToContinueBannerEnabled ? (
							<Box
								onClick={() => (contentContainerRef.current && scrollToContinueBannerEnabled) && contentContainerRef.current.scrollBy({top: videoContainerRef.current?.offsetHeight, behavior: "smooth"})}
								className={classes.scrollToContinueBanner}
							>
								<KeyboardDoubleArrowDownIcon /><Typography variant="h5">{t("please_scroll_to_continue", "Please scroll to continue")}</Typography>
							</Box>
						) : null
					}}
					onReady={handleVideoReady}
					ref={videoRef}
					lesson={lesson}
					currentTime={resumeTimeSecondsForPlayer}
					bitmovinPlayer={player}
					onVideoFinished={() => setVideoFinished(true)}
					onPlay={() => setVideoPlaying(true)}
					onPause={() => setVideoPlaying(false)}
					onEnded={() => { setVideoPlaying(false); setVideoFinished(true); }}
					onProgress={handleReactVideoProgress}
					onPlaybackTime={trackPlaybackTimeForFlush}
				/>
			</Box>
			<Box className={classes.tabs}>
				<Tabs
					variant="standard"
					value={tabSelected}
					onChange={(_e, tab) => setTab(tab)}
				>
					{MODULE_TABS.filter(e => e.value !== "comments" || memberedCohort?.comments).map(({label, value}) => (
						<Tab
							data-test-id={`module-tabs-${value?.toLocaleLowerCase()?.replace(" ", "")}`}
							key={value}
							value={value}
							label={label}
							className={cx(classes.tab, tabSelected === value ? classes.activeTab : "")}
						/>
					))}
				</Tabs>
			</Box>
			<CenteredContainer className={classes.centeredContainer}>
			{tabSelected === "overview" && (
				<>
						{lesson.complete && (
							<Alert severity="success" sx={{mb: 2}} variant="filled" >
								{t("lesson_completed_on", "Lesson completed on {{date}}", {
									date: lesson.completedAt
										? new Date(lesson.completedAt).toLocaleDateString("en-US", {month: "2-digit", day: "2-digit", year: "numeric"})
										: ""
								})}
							</Alert>
						)}
						<PageTitle className={cx(classes.title, !isSubtitleExpanded && "collapsed", needsTruncation && "hasButton")} title={lesson?.title || ""} subtitle={lesson?.subtitle || ""} />
						{lesson?.content && (
							<WYSIWYG className={classes.content}>
								{lesson.content}
							</WYSIWYG>
						)}
						{needsTruncation && (
							<Button
								className={classes.viewMoreButton}
								onClick={() => setIsSubtitleExpanded(!isSubtitleExpanded)}
								disableRipple
								disableTouchRipple
								aria-expanded={isSubtitleExpanded}
								aria-label={isSubtitleExpanded ? t("view_less", "View less") : t("view_more", "View more")}
							>
								{isSubtitleExpanded ? t("view_less", "View less") : t("view_more", "View more")}
								<ExpandMoreIcon className={cx("chevron", isSubtitleExpanded && "expanded")} aria-hidden="true" />
							</Button>
						)}

						{lesson?.quiz && (
							<MiniQuizzer
								key={lesson.enrollmentId ?? lesson.slug}
								disabledMessage="Please complete the video before taking the quiz"
								disabled={miniQuizzerDisabled}
								autoStart
								className={classes.miniQuizzer}
								enrollmentId={lesson.enrollmentId}
								onComplete={() => {
									setQuizComplete(true);
								}}
								quiz={lesson.quiz as any} // TODO: Update types
								onQuizResult={e => {
									setQuizComplete(!!e);
									const el = contentContainerRef.current;
									if (scrollToContinueBannerEnabled && el)
										setTimeout(() => {
											el.scrollTo({top: el.scrollHeight, behavior: "smooth"});
										}, 300);
								}}
							/>
						)}

						{lesson?.files && lesson.files.length > 0 && (
							<>
								<Typography className={classes.attachmentsSectionTitle} variant="h5">Files</Typography>
								<Files className={classes.files} files={lesson.files as any} />
							</>
						)}

						{courseSlug && lesson?.slug && !lesson.complete && (
							<Button
								style={{opacity: canComplete ? 1 : 0.5, width: "auto"}}
								onClick={() => {
									if (isCompletingLesson) return;
									if (handleCompleteCheck()) {
										handleCompletion();
									}
								}}
								className={classes.lessonCompleteBtn}
								variant={canComplete ? "contained" : "outlined"}
								disabled={isCompletingLesson}
								aria-busy={isCompletingLesson}
								startIcon={
									isCompletingLesson ? (
										<CircularProgress color="inherit" size={18} aria-hidden />
									) : undefined
								}
							>
								{t("mark_as_completed", "Mark as completed")}
								{!canComplete && !isCompletingLesson && (
									<LockIcon sx={{marginLeft: "8px", fontSize: "16px"}} />
								)}
							</Button>
						)}
						{courseSlug && lesson?.slug && lesson.complete && firstAvailableModule && (
							<Button
								onClick={() => navigate(`/course/${courseSlug}/${firstAvailableModule.type}/${firstAvailableModule?.slug}`)}
								className={classes.lessonCompleteBtn}
							>
								{t("rxd_next_lesson", "Next Lesson")}
							</Button>
						)}
					</>
				)}
				{tabSelected === "comments" && <CommentsSection className={classes.comments} lessonEnrollmentId={lesson?.enrollmentId} translations={commentsTranslations} />}
				{tabSelected === "notes" && <NotesSection className={classes.notesSection} disableTimeStamp={!lesson?.videoUrl} lessonEnrollmentId={lesson?.enrollmentId} videoRef={videoRef} translations={notesTranslations} onGoToModuleAction={(courseId, moduleId, moduleType, timestamp) => { navigate(`/course/${courseId}/${moduleType}/${moduleId}`, {state: {timestamp}}); }} />}
				<StreakDrawer
					open={openStreakDrawer && streakDays > 0}
					streakDays={streakDays}
					onContinue={handleClickContinue}
				/>
				<ResumeStreakDrawer
					open={isStreakPaused}
					onContinue={handleResumeStreakClose}
				/>
			</CenteredContainer>
			<CompletionCelebrationWrap
				open={openCelebration}
				onClose={handleCelebrationClose}
				courseTitle={courseDetails?.course?.title}
			/>
		</>
	);
};

